import { useCallback, useEffect, useRef } from "react";

import { cx } from "./cx";

/**
 * The theme dial: one card per palette, the chosen one centred, its neighbours peeking
 * above and below like the next marks on a wheel.
 *
 * The radio group is still the source of truth -- the same inputs the two-column grid
 * used, so `theme-option:has(input:checked)` keeps drawing the accent ring and a change
 * still goes through `selectTheme`. The dial only decides *which* radio is checked: it
 * turns one card per gesture, never free-scrolls.
 *
 * Transforms rather than a scroll container, deliberately. A fling in a scroll box is
 * measured in velocity, so a hard flick lands three themes away; here a wheel is
 * accumulated into a single step, a drag lands on the next card whatever its length, and
 * nothing inside the dial can scroll the page. Reduced motion swaps the card without the
 * movement -- the same card, not less of the dial.
 *
 * The one exception is the drag itself: the track follows the finger, because a dial that
 * refuses to move under the thumb reads as broken. It still only ever lands one step away.
 */

/** Wheel travel that counts as one step, and how long a step ignores the next one. */
const WHEEL_STEP = 10;
const WHEEL_LOCK_MS = 220;
/** Drag travel that counts as a step; anything shorter springs back to the same card. */
const DRAG_STEP = 28;
/** Drag travel that marks the gesture as a drag, so its release is not also a tap. */
const DRAG_SLOP = 6;

/** Generic over the palette id, so a caller's `ThemeId` reaches its own `selectTheme`. */
export interface ThemeDialProps<Id extends string = string> {
  themes: readonly { id: Id; name: string }[];
  /** The palette in use. */
  selected: Id;
  onSelect: (id: Id) => void;
}

function Caret({ up }: { up: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={up ? "M5 15 12 8l7 7" : "M5 9l7 7 7-7"} />
    </svg>
  );
}

export function ThemeDial<Id extends string>({ themes, selected, onSelect }: ThemeDialProps<Id>) {
  const dialRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const drag = useRef({ active: false, moved: false, startY: 0, startIndex: 0, offset: 0 });
  const wheelTotal = useRef(0);
  const lockedUntil = useRef(0);

  const index = Math.max(
    0,
    themes.findIndex((theme) => theme.id === selected),
  );

  /**
   * One card plus the gap, from layout boxes rather than painted ones: every card but the
   * centred one is scaled down, and measuring a painted box would move the track a few
   * pixels short on every step.
   */
  const cardStep = useCallback(() => {
    const track = trackRef.current;
    const first = track?.firstElementChild as HTMLElement | null;
    if (!track || !first) return 0;
    const gap = parseFloat(getComputedStyle(track).rowGap) || 0;
    return first.offsetHeight + gap;
  }, []);

  /** Put card `position` in the middle, `offset` pixels away while a finger drags it. */
  const place = useCallback(
    (offset = 0, position = index) => {
      const track = trackRef.current;
      if (!track) return;
      // The card is its content, so the window is told how tall that turned out to be.
      const card = track.firstElementChild as HTMLElement | null;
      if (card) dialRef.current?.style.setProperty("--dial-card", `${card.offsetHeight}px`);
      track.style.transform = `translateY(${-position * cardStep() + offset}px)`;
    },
    [cardStep, index],
  );

  /** Turn to a card, or spring back when it is already the one in the middle. */
  const turnBy = useCallback(
    (next: number) => {
      const target = Math.min(themes.length - 1, Math.max(0, next));
      if (target === index) place(0);
      else onSelect(themes[target].id);
    },
    [index, onSelect, place, themes],
  );

  // The card in the middle follows the selection, whichever way it changed: a tap, an
  // arrow key, a drag, or a theme restored from storage.
  useEffect(() => {
    place(0);
  }, [place]);

  useEffect(() => {
    const onResize = () => place(0);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [place]);

  /**
   * The wheel, natively and non-passively: React's own `onWheel` cannot cancel the page
   * scroll that a turn over the dial would otherwise also perform.
   */
  useEffect(() => {
    const dial = dialRef.current;
    if (!dial) return;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      if (Date.now() < lockedUntil.current) return;
      // Small trackpad deltas accumulate instead of being dropped.
      wheelTotal.current += event.deltaY;
      if (Math.abs(wheelTotal.current) < WHEEL_STEP) return;
      lockedUntil.current = Date.now() + WHEEL_LOCK_MS;
      turnBy(index + (wheelTotal.current > 0 ? 1 : -1));
      wheelTotal.current = 0;
    };
    dial.addEventListener("wheel", onWheel, { passive: false });
    return () => dial.removeEventListener("wheel", onWheel);
  }, [index, turnBy]);

  // Keyboard focus follows the card the dial turns to, so the group keeps a real focus
  // position for anyone stepping with the arrow keys.
  useEffect(() => {
    const dial = dialRef.current;
    if (!dial || !dial.contains(document.activeElement)) return;
    dial.querySelectorAll<HTMLInputElement>('input[name="theme"]')[index]?.focus();
  }, [index]);

  return (
    <div
      ref={dialRef}
      className={cx(
        "theme-dial",
        index === 0 && "at-start",
        index === themes.length - 1 && "at-end",
      )}
      onKeyDown={(event) => {
        const moves: Record<string, number> = {
          ArrowDown: 1,
          ArrowRight: 1,
          PageDown: 1,
          ArrowUp: -1,
          ArrowLeft: -1,
          PageUp: -1,
        };
        if (event.key in moves) {
          event.preventDefault();
          turnBy(index + moves[event.key]);
        } else if (event.key === "Home") {
          event.preventDefault();
          turnBy(0);
        } else if (event.key === "End") {
          event.preventDefault();
          turnBy(themes.length - 1);
        }
      }}
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        drag.current = {
          active: true,
          moved: false,
          startY: event.clientY,
          startIndex: index,
          offset: 0,
        };
        dialRef.current?.classList.add("is-dragging");
        dialRef.current?.setPointerCapture?.(event.pointerId);
      }}
      onPointerMove={(event) => {
        const state = drag.current;
        if (!state.active) return;
        state.offset = event.clientY - state.startY;
        if (Math.abs(state.offset) > DRAG_SLOP) state.moved = true;
        place(state.offset);
      }}
      onPointerUp={() => {
        const state = drag.current;
        if (!state.active) return;
        state.active = false;
        dialRef.current?.classList.remove("is-dragging");
        if (Math.abs(state.offset) > DRAG_STEP) {
          turnBy(state.startIndex + (state.offset < 0 ? 1 : -1));
        } else {
          place(0);
        }
        state.offset = 0;
      }}
      onPointerCancel={() => {
        drag.current.active = false;
        dialRef.current?.classList.remove("is-dragging");
        place(0);
      }}
      onClickCapture={(event) => {
        // A drag that ended over a card is not also a tap on it.
        if (!drag.current.moved) return;
        event.preventDefault();
        event.stopPropagation();
        drag.current.moved = false;
      }}
    >
      <span className="dial-arrow dial-arrow-up" aria-hidden="true">
        <Caret up />
      </span>
      <div className="theme-dial-window">
        <div className="theme-dial-track" ref={trackRef}>
        {themes.map((theme, position) => {
          const distance = position - index;
          const shown = theme.id === selected;
          return (
            <label
              key={theme.id}
              className="theme-option dial-item"
              data-offset={Math.abs(distance) > 2 ? undefined : String(distance)}
            >
              <input
                type="radio"
                name="theme"
                value={theme.id}
                checked={shown}
                onChange={() => onSelect(theme.id)}
                // The name is drawn inside an aria-hidden miniature, so the control
                // carries it again for anyone not reading the picture.
                aria-label={theme.name}
                className="sr-only"
              />
              <span className="theme-mini" data-palette={theme.id} aria-hidden="true">
                <span className="theme-mini-heading" />
                <span className="theme-mini-action">
                  {/* The name lives in the palette's own accent bar. */}
                  <span className="theme-mini-label">{theme.name}</span>
                </span>
                <span className="theme-mini-tiles">
                  <i />
                  <i />
                </span>
              </span>
            </label>
          );
        })}
        </div>
      </div>
      <span className="dial-arrow dial-arrow-down" aria-hidden="true">
        <Caret up={false} />
      </span>
    </div>
  );
}
